import React, { useState, useEffect } from 'react';
import { X, Save, Loader2, ChevronRight, ChevronLeft, Copy, Check, ExternalLink, Info, Database, CheckCircle2, CheckSquare, Square } from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { showError, showSuccess } from '../../../utils/toast';
import MultiSelectApiDropdown from '../../../components/MultiSelectApiDropdown';
import DBTypeIcon from '../../../components/DBTypeIcon';

interface DBConnectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    connectionToEdit?: any | null;
    agent_id?: string;
    client_id?: string;
}

interface DynamicInput {
    name: string;
    type: string;
    label: string;
    default?: any;
}

interface StepItem {
    id: string;
    title: string;
    description?: string;
    run_on?: string;
    copyable?: boolean;
    inputs?: DynamicInput[];
    commands?: string[];
    notes?: string[];
    docs?: string;
}

// Helper to parse deployments list for a given DB object
const getDeploymentsForDb = (dbObj: any): { id: string; label: string; versions?: string[] }[] => {
    if (!dbObj) return [];
    if (Array.isArray(dbObj.deployments)) {
        return dbObj.deployments.map((dep: any) => {
            if (typeof dep === 'string') return { id: dep, label: dep };
            return {
                id: dep.id || dep.value || dep.name || String(dep),
                label: dep.label || dep.name || dep.id || String(dep),
                versions: dep.versions
            };
        });
    }
    // Old format fallback: dbObj = { "self-hosted": ["12", "13"] }
    return Object.keys(dbObj)
        .filter(k => k !== 'selection_order' && k !== 'versions' && k !== 'deployments')
        .map(depKey => ({
            id: depKey,
            label: depKey,
            versions: Array.isArray(dbObj[depKey]) ? dbObj[depKey] : []
        }));
};

// Helper to parse versions list for a given DB object & deployment ID
const getVersionsForDbAndDep = (dbObj: any, depId: string): { value: string; label: string }[] => {
    if (!dbObj) return [];

    const deployments = getDeploymentsForDb(dbObj);
    const matchedDep = deployments.find(d => d.id === depId);

    // List of allowed version keys for this deployment (e.g. ["12", "13", "14"])
    const depVersionKeys: string[] = matchedDep?.versions || [];

    // Check if dbObj has a top-level versions array of objects: [{ value: "12", label: "12" }]
    let allVersions: { value: string; label: string }[] = [];
    if (Array.isArray(dbObj.versions)) {
        allVersions = dbObj.versions.map((v: any) => {
            if (typeof v === 'string') return { value: v, label: v };
            return {
                value: String(v.value || v.id || v),
                label: String(v.label || v.name || v.value || v)
            };
        });
    } else if (Array.isArray(dbObj[depId])) {
        // Old format fallback: dbObj[depId] = ["12", "13"]
        allVersions = dbObj[depId].map((v: any) => {
            if (typeof v === 'string') return { value: v, label: v };
            return {
                value: String(v.value || v.id || v),
                label: String(v.label || v.name || v.value || v)
            };
        });
    }

    if (depVersionKeys.length > 0 && allVersions.length > 0) {
        // Filter allVersions to only include ones allowed by depVersionKeys
        const filtered = allVersions.filter(v => depVersionKeys.includes(v.value));
        if (filtered.length > 0) return filtered;
    }

    if (allVersions.length > 0) return allVersions;

    // Fallback if depVersionKeys contains strings but allVersions was empty
    if (depVersionKeys.length > 0) {
        return depVersionKeys.map(v => ({ value: String(v), label: String(v) }));
    }

    return [];
};

const DBConnectionModal: React.FC<DBConnectionModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    connectionToEdit,
    client_id
}) => {
    // Stepper state: 0 = Selection, 1..N = Dynamic setup steps, N+1 = Final settings & Save
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [loadingOptions, setLoadingOptions] = useState<boolean>(false);
    const [loadingSetup, setLoadingSetup] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // Options from /db_monitor/setup/options
    const [rawOptions, setRawOptions] = useState<Record<string, Record<string, string[]>>>({});
    const [selectedDbType, setSelectedDbType] = useState<string>('');
    const [selectedDeployment, setSelectedDeployment] = useState<string>('');
    const [selectedVersion, setSelectedVersion] = useState<string>('');
    const [dbName, setDbName] = useState<string>('');
    const [connectionName, setConnectionName] = useState<string>('');

    // Dynamic setup steps from /db_monitor/setup
    const [setupSteps, setSetupSteps] = useState<StepItem[]>([]);
    const [dynamicInputs, setDynamicInputs] = useState<Record<string, any>>({});

    // Final step state
    const [schemaExtractionEnabled, setSchemaExtractionEnabled] = useState<boolean>(true);
    const [selectedAgents, setSelectedAgents] = useState<any[]>([]);

    // Copying status for commands
    const [copiedIndex, setCopiedIndex] = useState<string | null>(null);

    // Initial reset / Edit mode hydration
    useEffect(() => {
        if (!isOpen) return;

        // Reset wizard state
        setCurrentStep(0);
        setSelectedDbType('');
        setSelectedDeployment('');
        setSelectedVersion('');
        setDbName('');
        setConnectionName('');
        setSetupSteps([]);
        setDynamicInputs({});
        setSchemaExtractionEnabled(true);

        if (connectionToEdit) {
            setConnectionName(connectionToEdit.name || '');
            setSelectedDbType(connectionToEdit.db_type || '');
            setDbName(connectionToEdit.credential_data?.db_name || connectionToEdit.credential_data?.database || '');
            setSchemaExtractionEnabled(connectionToEdit.credential_data?.schema_extraction_enabled ?? true);
            setDynamicInputs({
                host: connectionToEdit.db_host || '',
                port: connectionToEdit.db_port || 5432,
                password: connectionToEdit.credential_data?.password || '',
                username: connectionToEdit.credential_data?.username || 'ovulscanner_monitor'
            });
        }

        fetchSetupOptions();
    }, [isOpen, connectionToEdit]);

    // Fetch initial agent assignments
    useEffect(() => {
        const fetchInitialAgents = async () => {
            if (!isOpen) return;

            const idsToFetch: number[] = [];
            if (connectionToEdit && connectionToEdit.agent_ids && connectionToEdit.agent_ids.length > 0) {
                idsToFetch.push(...connectionToEdit.agent_ids);
            }

            if (idsToFetch.length > 0) {
                try {
                    const query = { agent_id: { $in: idsToFetch } };
                    const params: any = { query: JSON.stringify(query) };
                    if (client_id) params.client_id = client_id;

                    const res = await baseapi.get('/db_monitor/agents/', { params });
                    if (res.data && res.data.status && res.data.data) {
                        const filtered = res.data.data.filter((item: any) =>
                            idsToFetch.some(id => id.toString() === item.agent_id?.toString())
                        );
                        setSelectedAgents(filtered.length > 0 ? filtered : idsToFetch.map(id => ({ agent_id: id, agent_name: id.toString() })));
                    } else {
                        setSelectedAgents(idsToFetch.map(id => ({ agent_id: id, agent_name: id.toString() })));
                    }
                } catch (e) {
                    setSelectedAgents(idsToFetch.map(id => ({ agent_id: id, agent_name: id.toString() })));
                }
            } else {
                setSelectedAgents([]);
            }
        };

        fetchInitialAgents();
    }, [connectionToEdit, isOpen, client_id]);

    // Step 1: Fetch options
    const fetchSetupOptions = async () => {
        setLoadingOptions(true);
        try {
            const res = await baseapi.get('/db_monitor/setup/options');
            if (res.data && res.data.status && res.data.data) {
                const raw = res.data.data;
                setRawOptions(raw);
                const dbKeys = Object.keys(raw);
                if (dbKeys.length > 0 && !selectedDbType) {
                    const defaultDb = dbKeys[0];
                    setSelectedDbType(defaultDb);
                    const deps = getDeploymentsForDb(raw[defaultDb]);
                    if (deps.length > 0) {
                        const defaultDep = deps[0].id;
                        setSelectedDeployment(defaultDep);
                        const vers = getVersionsForDbAndDep(raw[defaultDb], defaultDep);
                        if (vers.length > 0) {
                            setSelectedVersion(vers[vers.length - 1].value || vers[0].value);
                        }
                    }
                }
            }
        } catch (error: any) {
            showError(error.message || 'Failed to fetch database options');
        } finally {
            setLoadingOptions(false);
        }
    };

    // When DB type changes, reset deployment & version
    const handleSelectDbType = (db: string) => {
        setSelectedDbType(db);
        const deps = getDeploymentsForDb(rawOptions[db]);
        if (deps.length > 0) {
            const depId = deps[0].id;
            setSelectedDeployment(depId);
            const vers = getVersionsForDbAndDep(rawOptions[db], depId);
            setSelectedVersion(vers.length > 0 ? vers[vers.length - 1].value : '');
        } else {
            setSelectedDeployment('');
            setSelectedVersion('');
        }
    };

    // When deployment changes, reset version
    const handleSelectDeployment = (dep: string) => {
        setSelectedDeployment(dep);
        const vers = getVersionsForDbAndDep(rawOptions[selectedDbType], dep);
        setSelectedVersion(vers.length > 0 ? vers[vers.length - 1].value : '');
    };

    // Advance from Step 1 to fetch setup steps
    const handleProceedFromStep1 = async () => {
        if (!selectedDbType) {
            showError('Please select a database type');
            return;
        }
        if (!selectedDeployment) {
            showError('Please select a deployment mode');
            return;
        }
        if (!selectedVersion) {
            showError('Please select a database version');
            return;
        }
        if (!connectionName.trim()) {
            showError('Please enter a connection name');
            return;
        }

        setLoadingSetup(true);
        try {
            const params: any = {
                database: selectedDbType,
                deployment: selectedDeployment,
                version: selectedVersion,
            };
            if (dbName.trim()) {
                params.db_name = dbName.trim();
            }

            const res = await baseapi.get('/db_monitor/setup', { params });
            if (res.data && res.data.steps) {
                const steps: StepItem[] = res.data.steps;
                setSetupSteps(steps);

                // Populate initial default values for inputs across all steps
                const newInputs: Record<string, any> = { ...dynamicInputs };
                steps.forEach(step => {
                    step.inputs?.forEach(inp => {
                        if (newInputs[inp.name] === undefined && inp.default !== undefined) {
                            newInputs[inp.name] = inp.default;
                        }
                    });
                });
                setDynamicInputs(newInputs);
                setCurrentStep(1);
            } else {
                showError('Failed to load setup steps from server');
            }
        } catch (error: any) {
            showError(error.response?.data?.detail?.[0]?.msg || error.message || 'Failed to load setup steps');
        } finally {
            setLoadingSetup(false);
        }
    };

    // Input change handler for dynamic step inputs
    const handleDynamicInputChange = (name: string, value: any) => {
        setDynamicInputs(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Helper to replace {{key}} in command string
    const formatCommand = (cmd: string): string => {
        return cmd.replace(/\{\{(\w+)\}\}/g, (_, key) => {
            if (dynamicInputs[key] !== undefined && dynamicInputs[key] !== '') {
                return dynamicInputs[key];
            }
            return `{{${key}}}`;
        });
    };

    // Copy command text
    const handleCopy = (text: string, key: string) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(key);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    // Final Save Handler
    const handleSaveConnection = async () => {
        if (!connectionName.trim()) {
            showError('Please enter a connection name');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                name: connectionName.trim(),
                db_host: dynamicInputs.host || '',
                db_port: parseInt(dynamicInputs.port || '5432', 10),
                db_type: selectedDbType,
                credential_data: {
                    password: dynamicInputs.password || '',
                    ...(dbName ? { db_name: dbName } : {}),
                    schema_extraction_enabled: schemaExtractionEnabled,
                    username: 'ovulscanner_monitor',
                    ...dynamicInputs
                },
                agent_ids: selectedAgents.map(a => a.agent_id || a.id).filter(id => id != null)
            };

            const params = { client_id };
            let response;
            if (connectionToEdit && connectionToEdit.id) {
                response = await baseapi.put(`/db_monitor/connections/${connectionToEdit.id}`, payload, { params });
            } else {
                response = await baseapi.post('/db_monitor/connections/', payload, { params });
            }

            if (response.data.status || response.data) {
                showSuccess(connectionToEdit ? 'Connection updated successfully' : 'Connection added successfully');
                onSuccess();
                onClose();
            } else {
                showError(response.data.message || 'Failed to save connection');
            }
        } catch (error: any) {
            showError(error.response?.data?.detail?.[0]?.msg || error.message || 'An error occurred while saving');
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    // Calculate total steps: Step 0 (Selection) + N (setup steps) + Final Step (Save)
    const totalWizardSteps = 1 + setupSteps.length + 1;
    const isFinalStep = currentStep === setupSteps.length + 1;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 dark:bg-black/75 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/80 rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden flex flex-col h-[90vh] sm:h-[85vh]">

                {/* Top Modal Header */}
                <div className="flex justify-between items-center px-4 sm:px-6 py-3.5 sm:py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-orange-500/10 border border-orange-500/20 text-orange-500">
                            <Database className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">
                                {connectionToEdit ? 'Edit DB Connection' : 'Add DB Connection'}
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Database monitoring setup wizard</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-white p-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Split Content: Left Timeline Sidebar + Right Content View */}
                <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
                    
                    {/* Left Sidebar Timeline */}
                    <div className="w-full md:w-72 shrink-0 bg-gray-50/80 dark:bg-gray-900/90 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 p-4 sm:p-5 flex flex-col justify-between overflow-y-auto custom-scroll max-h-44 md:max-h-none">
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-4 px-1">
                                SETUP TIMELINE
                            </div>

                            <div className="relative pl-6 space-y-5 md:space-y-6 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-[2px] before:bg-gray-200 dark:before:bg-gray-800">
                                {/* Timeline Item 0: Selection */}
                                <div
                                    onClick={() => { if (currentStep > 0) setCurrentStep(0); }}
                                    className={`relative flex items-start gap-3 cursor-pointer group transition-all ${
                                        currentStep === 0 ? 'text-orange-600 dark:text-orange-400 font-bold' : currentStep > 0 ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'
                                    }`}
                                >
                                    <span className={`absolute -left-6 top-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                                        currentStep === 0
                                            ? 'bg-orange-500 text-white ring-4 ring-orange-500/20 shadow-lg shadow-orange-500/30'
                                            : currentStep > 0
                                            ? 'bg-emerald-500 text-white'
                                            : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-700'
                                    }`}>
                                        {currentStep > 0 ? <Check className="w-3 h-3 stroke-[3]" /> : '1'}
                                    </span>
                                    <div>
                                        <div className={`text-xs font-semibold ${currentStep === 0 ? 'text-orange-600 dark:text-orange-400 font-bold' : 'text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white'}`}>
                                            Database & Version
                                        </div>
                                        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                                            {selectedDbType ? `${selectedDbType} • v${selectedVersion || '?'}` : 'Select type & version'}
                                        </div>
                                    </div>
                                </div>

                                {/* Dynamic Setup Steps */}
                                {setupSteps.map((step, idx) => {
                                    const stepIndex = idx + 1;
                                    const isActive = currentStep === stepIndex;
                                    const isPassed = currentStep > stepIndex;
                                    const canClick = isPassed || isActive;

                                    return (
                                        <div
                                            key={step.id || idx}
                                            onClick={() => { if (canClick) setCurrentStep(stepIndex); }}
                                            className={`relative flex items-start gap-3 transition-all ${
                                                canClick ? 'cursor-pointer group' : 'cursor-not-allowed opacity-60'
                                            }`}
                                        >
                                            <span className={`absolute -left-6 top-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                                                isActive
                                                    ? 'bg-orange-500 text-white ring-4 ring-orange-500/20 shadow-lg shadow-orange-500/30'
                                                    : isPassed
                                                    ? 'bg-emerald-500 text-white'
                                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-700'
                                            }`}>
                                                {isPassed ? <Check className="w-3 h-3 stroke-[3]" /> : idx + 2}
                                            </span>
                                            <div>
                                                <div className={`text-xs font-semibold ${isActive ? 'text-orange-600 dark:text-orange-400 font-bold' : isPassed ? 'text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
                                                    {step.title}
                                                </div>
                                                {step.run_on && (
                                                    <span className="inline-block mt-0.5 px-1.5 py-0.2 text-[9px] font-mono rounded bg-blue-50 dark:bg-gray-800 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-gray-700">
                                                        {step.run_on}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Final Step Item */}
                                {setupSteps.length > 0 && (
                                    <div
                                        onClick={() => { if (currentStep === totalWizardSteps - 1) setCurrentStep(totalWizardSteps - 1); }}
                                        className={`relative flex items-start gap-3 transition-all ${
                                            currentStep >= setupSteps.length ? 'cursor-pointer group' : 'cursor-not-allowed opacity-60'
                                        }`}
                                    >
                                        <span className={`absolute -left-6 top-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                                            isFinalStep
                                                ? 'bg-orange-500 text-white ring-4 ring-orange-500/20 shadow-lg shadow-orange-500/30'
                                                : 'bg-gray-100 dark:bg-gray-800 text-gray-400 border border-gray-300 dark:border-gray-700'
                                        }`}>
                                            {totalWizardSteps}
                                        </span>
                                        <div>
                                            <div className={`text-xs font-semibold ${isFinalStep ? 'text-orange-600 dark:text-orange-400 font-bold' : 'text-gray-500 dark:text-gray-400'}`}>
                                                Agent & Options
                                            </div>
                                            <div className="text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">
                                                Schema & agent assignments
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Bottom Timeline Summary */}
                        <div className="p-3 bg-white dark:bg-gray-900/80 rounded-xl border border-gray-200 dark:border-gray-700 text-[11px] text-gray-600 dark:text-gray-400 mt-4 hidden md:block">
                            <span className="font-bold text-gray-800 dark:text-gray-300 block mb-0.5">Step Progress</span>
                            Step {currentStep + 1} of {totalWizardSteps}
                        </div>
                    </div>

                    {/* Right Content Panel */}
                    <div className="flex-1 flex flex-col justify-between overflow-hidden bg-white dark:bg-gray-900">
                        {/* Dynamic Step Content Area */}
                        <div className="p-4 sm:p-6 overflow-y-auto custom-scroll flex-1 space-y-6">

                            {/* ================= STEP 0: DB SELECTION ================= */}
                            {currentStep === 0 && (
                                <div className="space-y-6">
                                    {loadingOptions ? (
                                        <div className="flex flex-col items-center justify-center py-16">
                                            <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-3" />
                                            <p className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">Loading Database Options...</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* 1. Database Type Cards */}
                                            <div>
                                                <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider block mb-3">
                                                    Select Database Type
                                                </label>
                                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                                    {Object.keys(rawOptions).length > 0 ? (
                                                        Object.keys(rawOptions).map(dbKey => {
                                                            const isSelected = selectedDbType === dbKey;
                                                            return (
                                                                <button
                                                                    key={dbKey}
                                                                    type="button"
                                                                    onClick={() => handleSelectDbType(dbKey)}
                                                                    className={`p-4 rounded-xl border text-left flex flex-col items-center justify-center gap-3 transition-all cursor-pointer ${
                                                                        isSelected
                                                                            ? 'bg-orange-50 dark:bg-orange-500/10 border-orange-500 text-orange-600 dark:text-white shadow-lg shadow-orange-500/10 ring-1 ring-orange-500/50'
                                                                            : 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 text-gray-700 dark:text-gray-400'
                                                                    }`}
                                                                >
                                                                    <div className="p-2 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                                                                        <DBTypeIcon dbType={dbKey} className="w-8 h-8" />
                                                                    </div>
                                                                    <span className="text-xs font-bold capitalize text-gray-800 dark:text-gray-200">{dbKey}</span>
                                                                </button>
                                                            );
                                                        })
                                                    ) : (
                                                        /* Fallback cards if rawOptions API format differs */
                                                        ['postgresql', 'mysql', 'mongodb', 'redis'].map(dbKey => {
                                                            const isSelected = selectedDbType === dbKey;
                                                            return (
                                                                <button
                                                                    key={dbKey}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setSelectedDbType(dbKey);
                                                                        setSelectedDeployment('self-hosted');
                                                                        setSelectedVersion('16');
                                                                    }}
                                                                    className={`p-4 rounded-xl border text-left flex flex-col items-center justify-center gap-3 transition-all cursor-pointer ${
                                                                        isSelected
                                                                            ? 'bg-orange-50 dark:bg-orange-500/10 border-orange-500 text-orange-600 dark:text-white shadow-lg ring-1 ring-orange-500/50'
                                                                            : 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-400'
                                                                    }`}
                                                                >
                                                                    <DBTypeIcon dbType={dbKey} className="w-8 h-8" />
                                                                    <span className="text-xs font-bold capitalize text-gray-800 dark:text-gray-200">{dbKey}</span>
                                                                </button>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </div>

                                            {/* 2. Deployment Options */}
                                            {selectedDbType && (
                                                <div>
                                                    <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider block mb-3">
                                                        Deployment Type
                                                    </label>
                                                    <div className="flex flex-wrap gap-3">
                                                        {getDeploymentsForDb(rawOptions[selectedDbType]).map(dep => {
                                                            const isSelected = selectedDeployment === dep.id;
                                                            return (
                                                                <button
                                                                    key={dep.id}
                                                                    type="button"
                                                                    onClick={() => handleSelectDeployment(dep.id)}
                                                                    className={`px-4 py-2.5 rounded-xl border text-xs font-bold capitalize transition-all cursor-pointer ${
                                                                        isSelected
                                                                            ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                                                                            : 'bg-gray-50 dark:bg-gray-800/60 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                                    }`}
                                                                >
                                                                    {dep.label || dep.id}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {/* 3. Version Selector */}
                                            {selectedDbType && selectedDeployment && (
                                                <div>
                                                    <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider block mb-3">
                                                        Database Version
                                                    </label>
                                                    <div className="flex flex-wrap gap-2">
                                                        {getVersionsForDbAndDep(rawOptions[selectedDbType], selectedDeployment).map(ver => {
                                                            const isSelected = selectedVersion === ver.value;
                                                            return (
                                                                <button
                                                                    key={ver.value}
                                                                    type="button"
                                                                    onClick={() => setSelectedVersion(ver.value)}
                                                                    className={`px-3 py-1.5 rounded-lg border text-xs font-mono font-bold transition-all cursor-pointer ${
                                                                        isSelected
                                                                            ? 'bg-orange-100 dark:bg-orange-500/20 border-orange-500 text-orange-600 dark:text-orange-400'
                                                                            : 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                                                                    }`}
                                                                >
                                                                    {ver.label ? (ver.label.toLowerCase().startsWith('v') ? ver.label : `v${ver.label}`) : `v${ver.value}`}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {/* 4. Connection Name & Optional Database Name */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                                                <div className="space-y-1">
                                                    <label className="text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase">
                                                        Connection Name <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={connectionName}
                                                        onChange={(e) => setConnectionName(e.target.value)}
                                                        className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:border-orange-500 focus:bg-white dark:focus:bg-gray-800 transition-all"
                                                        placeholder="e.g. Production Postgres"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase">
                                                        Database Name (Optional)
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={dbName}
                                                        onChange={(e) => setDbName(e.target.value)}
                                                        className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:border-orange-500 focus:bg-white dark:focus:bg-gray-800 transition-all"
                                                        placeholder="e.g. my_target_db"
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* ================= STEP 1..N: DYNAMIC SETUP STEPS ================= */}
                            {currentStep > 0 && currentStep <= setupSteps.length && (
                                (() => {
                                    const step = setupSteps[currentStep - 1];
                                    if (!step) return null;

                                    return (
                                        <div className="space-y-6 animate-fadeIn">
                                            {/* Step Header */}
                                            <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-2">
                                                <div className="flex items-center justify-between gap-2">
                                                    <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                        <span className="px-2 py-0.5 rounded bg-orange-500/10 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 text-xs font-mono font-bold">
                                                            Step {currentStep}
                                                        </span>
                                                        {step.title}
                                                    </h3>
                                                    {step.run_on && (
                                                        <span className="px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 text-blue-700 dark:text-blue-400 text-[10px] uppercase font-bold tracking-wider">
                                                            Run on: {step.run_on}
                                                        </span>
                                                    )}
                                                </div>
                                                {step.description && (
                                                    <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                                        {step.description}
                                                    </p>
                                                )}
                                                {step.docs && (
                                                    <a
                                                        href={step.docs}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-600 dark:text-orange-400 hover:underline pt-1"
                                                    >
                                                        <span>Documentation</span>
                                                        <ExternalLink className="w-3 h-3" />
                                                    </a>
                                                )}
                                            </div>

                                            {/* Dynamic Inputs */}
                                            {step.inputs && step.inputs.length > 0 && (
                                                <div className="bg-gray-50/80 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-3">
                                                    <h4 className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                        Required Inputs for this Step
                                                    </h4>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        {step.inputs.map((inp) => (
                                                            <div key={inp.name} className="space-y-1">
                                                                <label className="text-[11px] font-bold text-gray-700 dark:text-gray-300">
                                                                    {inp.label || inp.name}
                                                                </label>
                                                                <input
                                                                    type={inp.type === 'number' ? 'number' : inp.type === 'password' ? 'password' : 'text'}
                                                                    value={dynamicInputs[inp.name] !== undefined ? dynamicInputs[inp.name] : (inp.default ?? '')}
                                                                    onChange={(e) => handleDynamicInputChange(inp.name, e.target.value)}
                                                                    className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:border-orange-500 font-mono"
                                                                    placeholder={inp.default ? `Default: ${inp.default}` : `Enter ${inp.label || inp.name}`}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Commands block */}
                                            {step.commands && step.commands.length > 0 && (
                                                <div className="space-y-2">
                                                    <div className="flex justify-between items-center">
                                                        <label className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                            Execute Commands
                                                        </label>
                                                        {step.copyable && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCopy(step.commands!.map(formatCommand).join('\n\n'), `step-${currentStep}`)}
                                                                className="flex items-center gap-1 px-2.5 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                                                            >
                                                                {copiedIndex === `step-${currentStep}` ? (
                                                                    <>
                                                                        <Check className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                                                                        <span className="text-green-600 dark:text-green-400">Copied!</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <Copy className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                                                                        <span>Copy All Commands</span>
                                                                    </>
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className='bg-gray-900 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl divide-y divide-gray-800 p-1'>
                                                        {step.commands.map((cmd, cIdx) => {
                                                            const formatted = formatCommand(cmd);
                                                            const cmdKey = `step-${currentStep}-cmd-${cIdx}`;
                                                            return (
                                                                <div key={cIdx} className="relative group overflow-hidden">
                                                                    <pre className="p-4 text-xs font-mono text-emerald-400 overflow-x-auto custom-scroll whitespace-pre-wrap break-all leading-relaxed">
                                                                        {formatted}
                                                                    </pre>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleCopy(formatted, cmdKey)}
                                                                        className="absolute top-2 right-2 p-1.5 bg-gray-800/80 hover:bg-gray-700 text-gray-300 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                                                                        title="Copy command"
                                                                    >
                                                                        {copiedIndex === cmdKey ? (
                                                                            <Check className="w-3.5 h-3.5 text-green-400" />
                                                                        ) : (
                                                                            <Copy className="w-3.5 h-3.5" />
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Notes */}
                                            {step.notes && step.notes.length > 0 && (
                                                <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-xl p-4 space-y-1.5">
                                                    <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 font-bold text-xs uppercase tracking-wider">
                                                        <Info className="w-4 h-4 shrink-0" />
                                                        <span>Important Notes</span>
                                                    </div>
                                                    <ul className="list-disc list-inside text-xs text-amber-800/90 dark:text-amber-200/90 space-y-1 leading-relaxed pl-1">
                                                        {step.notes.map((note, nIdx) => (
                                                            <li key={nIdx}>{note}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()
                            )}

                            {/* ================= FINAL STEP: AGENTS & OPTIONS ================= */}
                            {isFinalStep && (
                                <div className="space-y-6">
                                    <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-2">
                                        <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                            <CheckCircle2 className="w-4 h-4 text-green-500 dark:text-green-400" />
                                            Final Connection Setup
                                        </h3>
                                        <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                            Configure extraction options and assign target agents to monitor this database instance.
                                        </p>
                                    </div>

                                    {/* Summary Card */}
                                    <div className="bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-5 gap-3 text-xs">
                                        <div>
                                            <span className="text-[10px] font-bold text-gray-500 uppercase block">Name</span>
                                            <span className="font-bold text-gray-900 dark:text-white truncate block" title={connectionName}>{connectionName}</span>
                                        </div>
                                        <div>
                                            <span className="text-[10px] font-bold text-gray-500 uppercase block">Database</span>
                                            <span className="font-bold text-orange-600 dark:text-orange-400 capitalize">{selectedDbType}</span>
                                        </div>
                                        <div>
                                            <span className="text-[10px] font-bold text-gray-500 uppercase block">Version</span>
                                            <span className="font-mono text-gray-800 dark:text-gray-200">v{selectedVersion}</span>
                                        </div>
                                        <div>
                                            <span className="text-[10px] font-bold text-gray-500 uppercase block">Host</span>
                                            <span className="font-mono text-gray-800 dark:text-gray-200">{dynamicInputs.host || 'Not set'}</span>
                                        </div>
                                        <div>
                                            <span className="text-[10px] font-bold text-gray-500 uppercase block">Port</span>
                                            <span className="font-mono text-gray-800 dark:text-gray-200">{dynamicInputs.port || '5432'}</span>
                                        </div>
                                    </div>

                                    {/* Schema Extraction Checkbox */}
                                    <div
                                        onClick={() => setSchemaExtractionEnabled(!schemaExtractionEnabled)}
                                        className="p-4 bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 rounded-xl flex items-start gap-3 cursor-pointer select-none transition-all group"
                                    >
                                        <div className="shrink-0 mt-0.5">
                                            {schemaExtractionEnabled ? (
                                                <CheckSquare size={18} className="text-orange-500 fill-orange-500/10" />
                                            ) : (
                                                <Square size={18} className="text-gray-400 dark:text-gray-600 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                                            )}
                                        </div>
                                        <div className="space-y-0.5">
                                            <span className="text-xs font-bold text-gray-900 dark:text-gray-200 block">
                                                Enable Schema Extraction (`schema_extraction_enabled`)
                                            </span>
                                            <span className="text-[11px] text-gray-500 dark:text-gray-400 block leading-relaxed">
                                                Allow the monitoring agent to periodically extract schema metadata, tables, and column structures.
                                            </span>
                                        </div>
                                    </div>

                                    {/* MultiSelect Agents */}
                                    <div className="bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-2 relative z-50">
                                        <MultiSelectApiDropdown
                                            label="ASSIGN AGENT IDS"
                                            apiEndpoint="/db_monitor/agents/"
                                            idProperty="agent_id"
                                            displayProperty="agent_name"
                                            secondaryDisplayProperty="agent_ip"
                                            searchFields={['agent_name', 'agent_ip']}
                                            value={selectedAgents}
                                            onChange={setSelectedAgents}
                                            additionalParams={client_id ? { client_id } : {}}
                                            placeholder="Select Agent(s)"
                                            maxVisibleTags={5}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer Controls */}
                        <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/90 flex justify-between items-center shrink-0">
                            <button
                                type="button"
                                onClick={() => {
                                    if (currentStep > 0) setCurrentStep(prev => prev - 1);
                                    else onClose();
                                }}
                                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-transparent rounded-lg text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                {currentStep === 0 ? 'Cancel' : 'Back'}
                            </button>

                            {currentStep < totalWizardSteps - 1 ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (currentStep === 0) {
                                            handleProceedFromStep1();
                                        } else {
                                            setCurrentStep(prev => prev + 1);
                                        }
                                    }}
                                    disabled={loadingSetup || loadingOptions}
                                    className="px-5 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-xs font-bold flex items-center gap-2 transition-colors disabled:opacity-50 shadow-lg shadow-orange-600/20 cursor-pointer"
                                >
                                    {loadingSetup ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span>Loading Steps...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Next</span>
                                            <ChevronRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleSaveConnection}
                                    disabled={submitting}
                                    className="px-5 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-xs font-bold flex items-center gap-2 transition-colors disabled:opacity-50 shadow-lg shadow-orange-600/20 cursor-pointer"
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    <span>{connectionToEdit ? 'Update Connection' : 'Save Connection'}</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DBConnectionModal;
